import { useState, useMemo, useCallback } from 'react'
import { PaginationState } from '@tanstack/react-table'

export interface UseFrontendPaginationOptions {
  initialPageSize?: number
  initialPageIndex?: number
  initialSearchParams?: Record<string, string | string[]>
}

export function useFrontendPagination<T>(
  data: T[],
  options: UseFrontendPaginationOptions = {}
) {
  const { initialPageSize = 50, initialPageIndex = 0, initialSearchParams } = options

  const getPageIndex = (): number => {
    if (!initialSearchParams) return initialPageIndex
    const pageParam = initialSearchParams['page']
    if (pageParam === undefined || pageParam === null) return initialPageIndex
    const str = Array.isArray(pageParam) ? pageParam[0] : String(pageParam)
    const val = parseInt(str, 10)
    return isNaN(val) ? initialPageIndex : val
  }

  const getPageSize = (): number => {
    if (!initialSearchParams) return initialPageSize
    const pageSizeParam = initialSearchParams['pageSize']
    if (pageSizeParam === undefined || pageSizeParam === null) return initialPageSize
    const str = Array.isArray(pageSizeParam) ? pageSizeParam[0] : String(pageSizeParam)
    const val = parseInt(str, 10)
    return isNaN(val) ? initialPageSize : val
  }

  const [pagination, setPaginationState] = useState<PaginationState>({
    pageIndex: getPageIndex(),
    pageSize: getPageSize(),
  })

  const totalRecords = data.length
  const pageCount = Math.max(1, Math.ceil(totalRecords / pagination.pageSize))

  // Ensure pageIndex does not exceed available pages if data length shrinks
  const safePageIndex = Math.min(pagination.pageIndex, Math.max(0, pageCount - 1))

  const adjustedPagination = useMemo(() => {
    if (safePageIndex !== pagination.pageIndex) {
      return { ...pagination, pageIndex: safePageIndex }
    }
    return pagination
  }, [pagination, safePageIndex])

  const paginatedData = useMemo(() => {
    const start = adjustedPagination.pageIndex * adjustedPagination.pageSize
    const end = start + adjustedPagination.pageSize
    return data.slice(start, end)
  }, [data, adjustedPagination])

  const setPagination = useCallback((updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(adjustedPagination) : updater
    setPaginationState(newPagination)
  }, [adjustedPagination])

  return {
    paginatedData,
    pagination: adjustedPagination,
    setPagination,
    pageCount,
    totalRecords,
  }
}
